import React, { FunctionComponent, useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { navigation as navigationObject } from '../../lib/Navigation/Navigation.component';
import { useResponsive } from '@hooks';
import { useSessionStore } from '../../lib/Store/sessionStore.component';
import { isWeb } from '@commonStyle';
import { AutresProps } from './Autres.types';
import styles from './Autres.styles';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { InfoDevice } from '../../components/InfoDevice';
import { getInitials } from '../../lib/generique';

type MenuItem = {
  id: number;
  categorie: string;
  title: string;
  subtitle: string;
  route: string;
  icon: string;
  color: string;
  background: string;
  danger?: boolean;
};

export const Autres: FunctionComponent<AutresProps> = ({ navigation }: any) => {
  const { accessibleDatabases, getCurrentInfos, clearSession } = useSessionStore();
  const { currentDatabase, currentLogin } = getCurrentInfos();
  const isResponsive = useResponsive();

  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    items.push({
      id: 1,
      categorie: 'Mon compte',
      title: currentLogin?.nomClassement ?? 'Mon Profil',
      subtitle: currentLogin?.situations?.[0]?.serviceSituations?.[0]?.service?.libelle ?? '',
      route: 'Profil',
      icon: '',
      color: '#FFA500',
      background: '#FFF4E0',
    });

    if (accessibleDatabases.length > 1) {
      items.push({
        id: 2,
        categorie: 'Mon compte',
        title: 'Changer de collectivité',
        subtitle: 'Choix de la collectivité',
        route: 'Collectivite',
        icon: 'office-building-outline',
        color: '#FFA500',
        background: '#FFF4E0',
      });
    }

    if (currentDatabase?.fichesAgent && currentDatabase.fichesAgent.length > 1) {
      items.push({
        id: 3,
        categorie: 'Mon compte',
        title: 'Changer de compte',
        subtitle: 'Choix d’un compte',
        route: 'SelectionFicheAgent',
        icon: 'account-switch-outline',
        color: '#6C63FF',
        background: '#EEEAFE',
      });
    }

    items.push({
      id: 4,
      categorie: 'Mon compte',
      title: 'Se déconnecter',
      subtitle: '',
      route: 'Connexion',
      icon: 'logout',
      color: '#D64545',
      background: '#FDECEC',
      danger: true,
    });

    items.push({
      id: 5,
      categorie: 'Mon espace',
      title: 'Mes droits',
      subtitle: 'Soldes & compteurs',
      route: 'Droits',
      icon: 'shield-check-outline',
      color: '#4ECDC4',
      background: '#E5FAF8',
    });
     if(process.env.NODE_ENV === 'development'){
    items.push({
      id: 6,
      categorie: 'Mon espace',
     title: 'Espace valideur',
      subtitle: 'Demandes à traiter',
      route: 'EspaceValideur',
      icon: 'calendar-check-outline',
      color: '#10B981',
      background: '#ECFDF5',
    });
  }

    items.push({
      id: 7,
      categorie: 'Mon espace',
      title: 'Actualités',
      subtitle: 'Dernières nouvelles',
      route: 'Actualite',
      icon: 'newspaper-variant-outline',
      color: '#FF9F43',
      background: '#FFF3E6',
    });

    items.push({
      id: 8,
      categorie: 'Mon espace',
      title: 'Mes liens',
      subtitle: 'Liens utiles',
      route: 'Liens',
      icon: 'link-variant',
      color: '#7048E8',
      background: '#F0EBFF',
    });

    return items;
  }, [accessibleDatabases, currentDatabase?.fichesAgent, currentLogin]);

  const groupedByCategory = useMemo(() => {
    const map = new Map<string, MenuItem[]>();

    menuItems.forEach(item => {
      const key = item.categorie || 'Autres';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(item);
    });

    const categoriesInOrder: string[] = [];
    menuItems.forEach(item => {
      if (!categoriesInOrder.includes(item.categorie)) {
        categoriesInOrder.push(item.categorie);
      }
    });

    return categoriesInOrder.map(categorie => ({
      categorie,
      items: map.get(categorie) || [],
    }));
  }, [menuItems]);

  const handlePress = async (item: MenuItem) => {
    if (item.route === 'Connexion') {
      await clearSession();
      !isWeb
        ? navigationObject.replace('Connexion', navigation)
        : navigationObject.navigate('/', navigation);
      return;
    }

    navigationObject.navigate(item.route, navigation);
  };

  if (!isResponsive) {
    return <>{navigationObject.navigate('Actualite', navigation)}</>;
  }

  return (
    <View style={styles.container}>
      {groupedByCategory.map(section => (
        <View key={section.categorie}>
          <Text style={styles.sectionTitle}>{section.categorie.toUpperCase()}</Text>
          <View style={styles.list}>
            {section.items.map(item => (
              <TouchableOpacity
                key={item.id}
                activeOpacity={0.85}
                onPress={() => handlePress(item)}
                style={styles.menuCard}
              >
                {item.id === 1 ? (
                  <View style={styles.initialsAvatar}>
                    <Text style={styles.initialsAvatarText}>
                      {getInitials(currentLogin?.nomClassement || '')}
                    </Text>
                  </View>
                ) : (
                  <View style={[styles.iconWrap, { backgroundColor: item.background }]}>
                    <MaterialCommunityIcons
                      name={item.icon as any}
                      size={22}
                      color={item.color}
                    />
                  </View>
                )}

                <View style={styles.menuText}>
                  <Text style={[styles.menuTitle, item.danger && styles.dangerText]}>
                    {item.title}
                  </Text>
                  {!!item.subtitle && (
                    <Text style={styles.menuSubtitle}>{item.subtitle}</Text>
                  )}
                </View>

                <MaterialCommunityIcons name="chevron-right" size={24} color="#D7D9E0" />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ))}

      <View style={{ marginTop: 15 }}>
        <InfoDevice />
      </View>
    </View>
  );
};
